import { Button } from '@zillow/constellation';
import { useEffect, useState } from 'react';

import UpsellModal from '../UpsellModal';
import { createUpsell, viewUpsell } from '../../api/api';
import { TrackingIdType, UpsellEntryPointType, UpsellType } from '../../types';
import type { TrackClickstreamEventFn } from '../../utils/ClickstreamEvents';
import { VIEW_LEASE_UPSELL } from '../../utils/ClickstreamEvents';

type PostLeaseSigningUpsellProps = {
  apiUrl: string;
  leaseId: string;
  nonUpsellButton: JSX.Element;
  onClose: () => void;
  onUpsellLoadingComplete: (shouldDisplayUpsell: boolean) => void;
  trackClickstreamEvent: TrackClickstreamEventFn;
};

const TRACKING_ID_TYPE = TrackingIdType.LEASE_ID;
const UPSELL_TYPE = UpsellType.LEASE_SIGNED_FLOW;
const UPSELL_ENTRY_POINT_TYPE = UpsellEntryPointType.LEASES_COMPLETED_SIGNING;

const PostLeaseSigningUpsell = ({
  apiUrl,
  leaseId,
  nonUpsellButton,
  onClose,
  onUpsellLoadingComplete,
  trackClickstreamEvent,
}: PostLeaseSigningUpsellProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [upsellInfo, setUpsellInfo] = useState({
    isInsuranceRequiredForLease: false,
    isInsuranceRequiredForListing: false,
    shouldDisplayUpsell: false,
    monthlyPriceEstimate: '',
    redirectUrl: '',
    upsellId: '',
    zip: '',
  });

  useEffect(() => {
    const getUpsellInfo = async () => {
      const data = await createUpsell(UPSELL_TYPE, TRACKING_ID_TYPE, leaseId, apiUrl);
      const { id: upsellId, shouldDisplayUpsell, url: redirectUrl } = data;

      if (shouldDisplayUpsell) {
        setUpsellInfo({
          upsellId,
          redirectUrl,
          ...data,
        });
      }

      setIsLoading(false);
      onUpsellLoadingComplete(shouldDisplayUpsell);
    };

    getUpsellInfo();
  }, [leaseId]);

  const onUpsellView = () => {
    trackClickstreamEvent(VIEW_LEASE_UPSELL(UPSELL_ENTRY_POINT_TYPE));
    return viewUpsell(upsellInfo.upsellId, TRACKING_ID_TYPE, apiUrl);
  };

  const showUpsellIfEligible = async () => {
    await onUpsellView();
    setOpenDialog(true);
  };

  const closeUpsellModal = () => {
    setOpenDialog(false);
    return onClose();
  };

  if (isLoading || !upsellInfo.shouldDisplayUpsell) {
    return nonUpsellButton;
  }

  return (
    <>
      <UpsellModal
        apiUrl={apiUrl}
        isOpen={openDialog}
        onClose={closeUpsellModal}
        trackingIdType={TRACKING_ID_TYPE}
        upsellEntryPointType={UPSELL_ENTRY_POINT_TYPE}
        upsellInfo={upsellInfo}
        trackClickstreamEvent={trackClickstreamEvent}
      />
      <Button data-testid="post-lease-signing-show-upsell-btn" buttonType="primary" onClick={showUpsellIfEligible}>
        Next
      </Button>
    </>
  );
};

export default PostLeaseSigningUpsell;
